import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import SearchableSelect from '../components/SearchableSelect';
import { useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/ProcurementOrderForm.jsx. Posts
// to `/purchase-requests/{id}/purchase-order`
// (ProcurementFulfillmentController::storePurchaseOrder) — only approved
// requests without an existing order show up in the picker (`for_po=1`,
// see PurchaseRequestController::index()).
interface OrderFormState {
  supplier_id: string | number;
  vendor_name: string;
  amount: string;
  delivery_date: string;
  payment_terms: string;
}

const emptyForm: OrderFormState = { supplier_id: '', vendor_name: '', amount: '', delivery_date: '', payment_terms: '' };

interface RequestOption {
  id: number;
  reference: string;
  item_name: string;
}

interface SupplierOption {
  id: number;
  business_name: string;
  contact_person: string | null;
  [key: string]: unknown;
}

type FormErrors = Record<string, string[]>;

export function ProcurementOrderForm() {
  const { settings } = useConfig();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<RequestOption[] | null>(null);
  const [purchaseRequestId, setPurchaseRequestId] = useState('');
  const [form, setForm] = useState<OrderFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ data: RequestOption[] }>('/purchase-requests', { params: { for_po: 1, per_page: 100 } })
      .then((res) => setRequests(res.data.data));
  }, []);

  const set =
    (key: keyof OrderFormState) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [key]: e.target.value });

  const selectSupplier = (id: string | number, supplier?: SupplierOption) => {
    setForm({ ...form, supplier_id: id, vendor_name: supplier?.business_name ?? '' });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!purchaseRequestId) {
      setErrors({ general: ['Select a purchase request to issue an order against.'] });
      return;
    }
    if (!form.supplier_id) {
      setErrors({ general: ['Select a supplier to issue an order against.'] });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/purchase-requests/${purchaseRequestId}/purchase-order`, form);
      navigate('/procurement/orders');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Add order</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
          <label className="span-2">
            Purchase request *
            <select value={purchaseRequestId} onChange={(e) => setPurchaseRequestId(e.target.value)} required>
              <option value="">
                {requests === null ? 'Loading…' : 'Select an approved request without an order yet…'}
              </option>
              {requests?.map((pr) => (
                <option key={pr.id} value={pr.id}>
                  {pr.reference} — {pr.item_name}
                </option>
              ))}
            </select>
            {requests?.length === 0 && (
              <small className="muted">No approved requests are currently awaiting a purchase order.</small>
            )}
          </label>
          <label>
            Supplier *
            <SearchableSelect<SupplierOption>
              endpoint="/suppliers"
              value={form.supplier_id}
              onChange={(id, supplier) => selectSupplier(id, supplier)}
              getLabel={(s) => s.business_name}
              getSubLabel={(s) => s.contact_person ?? ''}
              placeholder="Search suppliers…"
            />
            {errors.supplier_id && <small className="error">{errors.supplier_id[0]}</small>}
            {errors.vendor_name && <small className="error">{errors.vendor_name[0]}</small>}
          </label>
          <label>
            Amount ({settings.currency}) *
            <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required />
            {errors.amount && <small className="error">{errors.amount[0]}</small>}
          </label>
          <label>
            Delivery date
            <input type="date" value={form.delivery_date} onChange={set('delivery_date')} />
          </label>
          <label>
            Payment terms
            <input value={form.payment_terms} onChange={set('payment_terms')} placeholder="e.g. Net 30" />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/procurement/orders">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
